import { useEffect, useRef } from "react";
import { SortOrder } from "primereact/api";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { UserRole } from "../../app/userSlice";
import { resetUsersListFilters, setUsersListFilters } from "../../app/usersListFilterSlice";

// The UsersList page's filter/sort/page state, saved so the values are restored
// when the user navigates away and back during the same login session.
//
// Storage, lifetime and per-user ownership are all decided by the slice through
// the shared settings model (see userSettings.ts). Most importantly, the values
// are owned by a user id and handed over when the signed-in user changes, so an
// admin's filters never follow them into an impersonated session - where the
// district or school they name is often not visible, and breaks this page. That
// handover lives in the slice rather than here because it has to happen even
// while this page is unmounted, which it always is across an impersonation
// handoff.
//
// The reset below is the narrower case the handover cannot see: the same user id
// coming back with a different role, where the filter UI itself differs (e.g.
// School Admin vs. District/State Admin) and the old values no longer fit it.
export function useUsersListFilters() {
	const dispatch = useAppDispatch();
	const { role } = useAppSelector((state) => state.user);

	const filters = useAppSelector((state) => state.usersListFilter.values);

	const previousRole = useRef<UserRole | null>(role);

	useEffect(() => {
		if (previousRole.current !== role) {
			dispatch(resetUsersListFilters());
		}
		previousRole.current = role;
	}, [role, dispatch]);

	return {
		...filters,
		setSearchFilter: (value: string) => dispatch(setUsersListFilters({ searchFilter: value })),
		setDistrictFilter: (value: number) =>
			dispatch(setUsersListFilters({ districtFilter: value })),
		setSchoolFilter: (value: number) => dispatch(setUsersListFilters({ schoolFilter: value })),
		setRoleFilter: (value: string) => dispatch(setUsersListFilters({ roleFilter: value })),
		setIncludeInactive: (value: boolean) =>
			dispatch(setUsersListFilters({ includeInactive: value })),
		setSortField: (value: string) => dispatch(setUsersListFilters({ sortField: value })),
		setSortOrder: (value: SortOrder) => dispatch(setUsersListFilters({ sortOrder: value })),
		setPage: (value: number) => dispatch(setUsersListFilters({ page: value })),
	};
}
